// Großer Zuweisungs-Dialog: die L6-1-Erweiterung (Gruppe-Pflicht + optionaler Bearbeiter + Self-Register)
// hebt die ohnehin hohe Komplexität dieses bestehenden Dialogs über die Schwelle; Verhalten ist per Vitest
// (23 Tests) abgesichert. Extraktion wäre ein eigenes Refactor (out-of-scope für L6-1).
// fallow-ignore-file complexity
import { useState, useEffect } from "react";
import { useDateLocale } from "@/hooks/useDateLocale";
import { useTranslation } from "react-i18next";
import { CalendarDays, Loader2, StickyNote, Users } from "lucide-react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AssignedGroupField } from "@/components/common/AssignedGroupField";
import { DetailSection } from "@/components/ui/detail-layout";
import { DueDatePicker } from "@/components/common/DueDatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { TablesUpdate } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { notifyAssignment, notifyCreatorOfAssignment } from "@/lib/notifications";
import { useDialogData, AssignableProfile } from "@/hooks/useDialogData";
import { getProfileDisplayName } from "@/lib/profileUtils";
import { ProfileSelect } from "@/components/common/ProfileSelect";
import { TeamReassignSelect } from "@/components/common/TeamReassignSelect";
import { UserCheck } from "lucide-react";
import { useTeamGroups } from "@/hooks/useTeamGroups";
import { useGroupMembers } from "@/hooks/useGroupMembers";

interface Inspection {
  id: string;
  assigned_to: string | null;
  assigned_group_id?: string | null;
  position_id: string | null;
  due_date: string | null;
  notes: string | null;
  status: string;
  created_by?: string | null;
  team_id?: string | null;
  checklists?: {
    name: string;
    version: string | null;
  };
}

interface EditInspectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inspection: Inspection | null;
  onUpdated: () => void;
}

export function EditInspectionDialog({
  open,
  onOpenChange,
  inspection,
  onUpdated,
}: EditInspectionDialogProps) {
  const { t } = useTranslation();
  const { user, isQualitaetsmanager } = useAuth();

  const dateLocale = useDateLocale();

  // Use consolidated dialog data hook (only positions and profiles needed for edit)
  const { positions, profiles, isLoading } = useDialogData({
    loadChecklists: false, // Checklist is fixed for existing inspections
    loadPositions: true,
    loadProfiles: true,
    enabled: open,
  });

  const [saving, setSaving] = useState(false);

  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date>();
  const [notes, setNotes] = useState("");
  const [createdBy, setCreatedBy] = useState<string>("");
  const [teamId, setTeamId] = useState<string>("");

  // TES-1867 (L6-1): Fachbereich-Gruppe (Pflicht) + optionaler Bearbeiter (gefüllt aus group_members).
  const { groups } = useTeamGroups(teamId || inspection?.team_id || null, open);
  const { members } = useGroupMembers(selectedGroup, open && !!selectedGroup);
  const memberIds = new Set(members.map((m) => m.user_id));
  const assigneeProfiles = selectedGroup
    ? profiles.filter((p) => memberIds.has(p.user_id))
    : profiles;
  const canSelfAssign = !!user?.id && !!selectedGroup && memberIds.has(user.id) && !selectedUser;

  useEffect(() => {
    if (inspection && open) {
      setSelectedUser(inspection.assigned_to || "");
      setSelectedGroup(inspection.assigned_group_id || "");
      setSelectedPosition(inspection.position_id || "");
      setDueDate(inspection.due_date ? new Date(inspection.due_date) : undefined);
      setNotes(inspection.notes || "");
      setCreatedBy(inspection.created_by || "");
      setTeamId(inspection.team_id || "");
    }
  }, [inspection, open]);

  const handleUpdate = async () => {
    if (!inspection) return;

    const previousAssignedTo = inspection.assigned_to;

    // TES-1867 (L6-1): die Fachbereich-Gruppe ist Pflicht; der individuelle Bearbeiter ist optional.
    if (!selectedGroup) {
      toast.error(t("inspections.assignmentSection.groupRequired"), {
        description: t("inspections.editDialog.validationErrorDesc"),
      });
      return;
    }

    setSaving(true);
    try {
      const updateData: TablesUpdate<"inspections"> = {
        assigned_group_id: selectedGroup,
        assigned_to: selectedUser || null,
        position_id: selectedPosition || null,
        due_date: dueDate?.toISOString() || null,
        notes: notes || null,
      };

      if (teamId && teamId !== inspection.team_id) {
        updateData.team_id = teamId;
      }

      // QM can change the creator
      if (isQualitaetsmanager && createdBy) {
        updateData.created_by = createdBy;
      }

      const { error } = await supabase
        .from("inspections")
        .update(updateData)
        .eq("id", inspection.id);

      if (error) throw error;

      // Notify new assignee if changed
      const checklistName = inspection.checklists?.name || t("inspections.title");
      if (selectedUser && selectedUser !== previousAssignedTo) {
        notifyAssignment(selectedUser, "inspection", checklistName, inspection.id);

        // Notify creator if different from new assignee and current user
        // Use already-available createdBy state instead of re-fetching from DB
        if (createdBy && createdBy !== selectedUser && createdBy !== user?.id) {
          const assigneeProfile = profiles.find((p) => p.user_id === selectedUser);
          const assigneeName = assigneeProfile ? getProfileName(assigneeProfile) : undefined;
          notifyCreatorOfAssignment(
            createdBy,
            "inspection",
            checklistName,
            inspection.id,
            assigneeName,
          );
        }
      }

      toast.success(t("inspections.editDialog.updated"), {
        description: t("inspections.editDialog.updatedDesc"),
      });

      // Show cascade info if team was changed
      if (teamId && teamId !== inspection.team_id) {
        toast.info(t("inspections.editDialog.teamReassignCascadeInfo"));
      }

      onOpenChange(false);
      onUpdated();
    } catch (error) {
      logger.error("Error updating inspection:", error);
      toast.error(t("inspections.editDialog.updateError"), {
        description: t("inspections.editDialog.updateErrorDesc"),
      });
    } finally {
      setSaving(false);
    }
  };

  const getProfileName = (profile: AssignableProfile) => getProfileDisplayName(profile);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* TES-1998: Edit spricht dieselbe visuelle Familie wie die Detail-Ansicht —
          breite Flaeche + Sektions-Cards (@plasser/ui DetailSection) statt schmalem Formular. */}
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("inspections.editDialog.title")}</DialogTitle>
          <DialogDescription>
            {inspection?.checklists?.name && (
              <span className="font-medium text-foreground">
                {inspection.checklists.name}
                {inspection.checklists.version ? ` (v${inspection.checklists.version})` : ""}
              </span>
            )}
            {" – "}
            {t("inspections.editDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 py-4 md:grid-cols-2">
            <DetailSection
              icon={<Users className="h-5 w-5 text-primary" aria-hidden="true" />}
              title={t("inspections.editDialog.sectionAssignment")}
              className="space-y-4"
            >
              {/* TES-1867 (L6-1): Fachbereich-Gruppe (Pflicht) — geteiltes Feld (Fallow-Dedup) */}
              <AssignedGroupField
                id="group"
                label={t("inspections.assignmentSection.groupLabel")}
                placeholder={t("inspections.assignmentSection.groupPlaceholder")}
                value={selectedGroup}
                groups={groups}
                onChange={(next) => {
                  setSelectedGroup(next);
                  // Bearbeiter zurücksetzen, wenn die Gruppe wechselt (er muss Mitglied der neuen Gruppe sein).
                  if (next !== selectedGroup) setSelectedUser("");
                }}
              />

              {/* Individueller Bearbeiter (optional, gefüllt aus group_members der gewählten Gruppe) */}
              <div className="space-y-2">
                <Label htmlFor="user">{t("inspections.assignmentSection.assigneeLabel")}</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <ProfileSelect
                      id="user"
                      profiles={assigneeProfiles}
                      value={selectedUser}
                      onValueChange={setSelectedUser}
                      placeholder={t("inspections.assignmentSection.assigneePlaceholder")}
                      assignmentContext="inspections"
                      provisionRole="tester"
                    />
                  </div>
                  {canSelfAssign && user?.id && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedUser(user.id)}
                      data-testid="inspection-self-assign"
                    >
                      <UserCheck className="mr-1 h-3.5 w-3.5" />
                      {t("inspections.assignmentSection.selfAssign")}
                    </Button>
                  )}
                </div>
              </div>

              {/* Created By - only for QM */}
              {isQualitaetsmanager && (
                <div className="space-y-2">
                  <Label htmlFor="createdBy">{t("inspections.editDialog.createdBy")}</Label>
                  <ProfileSelect
                    id="createdBy"
                    profiles={profiles}
                    value={createdBy}
                    onValueChange={setCreatedBy}
                    placeholder={t("inspections.editDialog.selectCreator")}
                    enableAzureSearch
                    assignmentContext="inspections"
                    provisionRole="tester"
                  />
                </div>
              )}

              {/* Team Reassignment */}
              <TeamReassignSelect
                value={teamId}
                onChange={setTeamId}
                createdBy={inspection?.created_by ?? null}
                currentUserId={user?.id}
              />
            </DetailSection>

            <DetailSection
              icon={<CalendarDays className="h-5 w-5 text-primary" aria-hidden="true" />}
              title={t("inspections.editDialog.sectionPlanning")}
              className="space-y-4"
            >
              {/* Position Selection */}
              <div className="space-y-2">
                <Label htmlFor="position">{t("inspections.editDialog.positionOptional")}</Label>
                <Select
                  value={selectedPosition || "__none__"}
                  onValueChange={(v) => setSelectedPosition(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger id="position">
                    <SelectValue placeholder={t("inspections.editDialog.selectPosition")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {t("inspections.editDialog.noPosition")}
                    </SelectItem>
                    {positions.map((position) => (
                      <SelectItem key={position.id} value={position.id}>
                        {position.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <Label>{t("inspections.editDialog.dueDateOptional")}</Label>
                <DueDatePicker
                  value={dueDate}
                  onChange={setDueDate}
                  locale={dateLocale}
                  placeholder={t("inspections.editDialog.selectDate")}
                />
              </div>
            </DetailSection>

            {/* Notes */}
            <DetailSection
              icon={<StickyNote className="h-5 w-5 text-primary" aria-hidden="true" />}
              title={t("inspections.editDialog.notesOptional")}
              className="md:col-span-2"
            >
              <Textarea
                id="notes"
                aria-label={t("inspections.editDialog.notesOptional")}
                placeholder={t("inspections.editDialog.notesPlaceholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </DetailSection>
          </div>
        )}

        {/* Sticky Action-Bar: bleibt beim Scrollen des Dialogs sichtbar (kein abgeschnittener
            Speichern-Button auf kleinen Viewports). */}
        <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 border-t border-outline-variant bg-background px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleUpdate} disabled={saving || isLoading}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
